import {
  columnMinAutoWidth,
  getColumnInitialSizes, getMetadataByCoords,
  getRowHeaders, getSelectionRectangle, getTableRowMetadata,
  getTopHeaderRows, getTreeCodeToIds, minColumnWidth, selectionToClipboard, tableContentWidthCorrection,
  tableMinHeight, tableDefaultHeight
} from './utils'
import { validate } from './validation'
import findIndex from 'lodash/findIndex'

const defaultEditorValues = {
  currentOriginalData: '',
  currentChangedData: '',
  inputSelectionStart: -1,
  inputSelectionEnd: -1,
  // Function that will verify that saved value is correct and format it for saving, 'invalid' string will prevent saving
  persistTransform: null,
  reverseTransformed: false,
  allowSelectionChange: true,
  enteredChar: ''
}

const initialState = {
  readOnly: false,
  fixedHeader: false,
  headerOffset: 0,
  tableWidth: 1600,
  tableHeight: tableDefaultHeight,
  mouseDown: false,
  editorActive: false,
  editorMode: 'none', // or click or enter or char (based on style of entering editor) or cancelled
  currentCol: null,
  currentRow: null,
  currentColTreeCode: null,
  currentRowTreeCode: null,
  selectionCol: null,
  selectionRow: null,
  topHeaderRows: [],
  topHeaderRowCount: 1,
  sideHeaderColumnCount: 0,
  columnSizes: [],
  rowSizes: {},
  tableMetadata: {},
  metadata: null,
  dataItems: [],
  colIds: {},
  rowIds: {},
  collapsedRows: [],
  hiddenRows: [],
  changedCells: [],
  scrollToRowIndex: -1,
  ...defaultEditorValues
}

function reducer (state, action) {
  switch (action.type) {
    case 'init': {
      const {
        persistenceId,
        sourceHeaders,
        sourceRowHeaders,
        cellMetadata,
        defaultMetadata,
        fixedHeader,
        readOnly,
        localizeValue
      } = action
      let { currentCol, currentRow, currentColTreeCode, currentRowTreeCode, tableWidth } = state
      const rowTitle = { ...(sourceRowHeaders && sourceRowHeaders['-1']), ...defaultMetadata }

      const topHeaderRows = getTopHeaderRows(sourceHeaders, rowTitle, readOnly, defaultMetadata, localizeValue)
      const topHeaderRowCount = topHeaderRows.length
      const topHeaderBottomRow = topHeaderRows[topHeaderRows.length - 1] || []
      const columnCount = topHeaderBottomRow.length
      // flag cell as most bottom cell of the top header
      topHeaderBottomRow.forEach((x, i) => {
        if (x.yCoord !== (topHeaderRowCount - 1)) {
          topHeaderRows[x.yCoord][i].topHeaderMostBottom = true
        } else {
          x.topHeaderMostBottom = true
        }
      })
      const rowHeaders = rowTitle && rowTitle.children && getRowHeaders(sourceRowHeaders, topHeaderRows.length, readOnly, localizeValue)
      const columnSizes = (persistenceId && state.persistenceId !== persistenceId) || state.columnSizes === initialState.columnSizes
        ? getColumnInitialSizes(columnCount, tableWidth + tableContentWidthCorrection, action.columnSizes) // initial auto-size
        : getColumnSizes(state.columnSizes, columnCount, state.topHeaderRows[state.topHeaderRowCount - 1], topHeaderBottomRow)
      const metadata = [
        ...topHeaderRows,
        ...rowHeaders.map((rowHeader, rowIndex) => getTableRowMetadata(rowIndex + topHeaderRowCount, topHeaderBottomRow, rowHeader, cellMetadata, defaultMetadata))
      ]

      if (currentColTreeCode) {
        const columnMetadata = topHeaderBottomRow.find(x => x.col === currentColTreeCode)
        if (columnMetadata) {
          currentCol = columnMetadata.xCoord
        } else {
          currentCol = null
          currentColTreeCode = null
        }
      }
      if (currentRowTreeCode) {
        const rowMetadata = rowHeaders.find(x => x.row === currentRowTreeCode)
        if (rowMetadata) {
          currentRow = rowMetadata.yCoord
        } else {
          currentRow = null
          currentRowTreeCode = null
        }
      }

      return {
        ...state,
        persistenceId,
        readOnly,
        currentColTreeCode,
        currentRowTreeCode,
        currentCol,
        currentRow,
        topHeaderRows,
        topHeaderRowCount,
        sideHeaderColumnCount: rowHeaders ? 1 : 0,
        metadata,
        colIds: getTreeCodeToIds(sourceHeaders),
        rowIds: getTreeCodeToIds(sourceRowHeaders),
        tableHeight: Math.max(tableMinHeight, action.tableHeight || tableDefaultHeight),
        columnSizes,
        fixedHeader
      }
    }
    case 'closeEditor': {
      return { ...state, editorActive: false, editorMode: 'none' }
    }
    case 'setData': {
      return {
        ...state,
        dataItems: action.sourceData || []
      }
    }
    case 'scrollToRow': {
      if (state.scrollToRowIndex !== action.scrollToRow) {
        let scrollToRowIndex = state.scrollToRowIndex
        if (typeof action.scrollToRow === 'number' && action.scrollToRow > 0) {
          scrollToRowIndex = action.scrollToRow
        } else if (typeof action.scrollToRow === 'string') {
          // search by row treeCode
          scrollToRowIndex = findIndex(state.metadata, x => x[0].row === action.scrollToRow)
        }
        if (state.scrollToRowIndex !== scrollToRowIndex && scrollToRowIndex >= 0) {
          return { ...state, scrollToRowIndex: scrollToRowIndex }
        }
      }
      break
    }
    case 'scroll': {
      return {
        ...state,
        headerOffset: action.scrollLeft
      }
    }
    case 'mouseDown': {
      const { x, y } = action
      const clickIn = !state.readOnly && state.currentCol === action.x && state.currentRow === action.y
      const originalData = getCurrentItemData(state)
      // Saves current changes when exiting a cell
      if (!clickIn) {
        state = updateChangedCell(state)
        if (!state.allowSelectionChange) {
          return state
        }
      }
      const currentMetadata = getMetadataByCoords(state.metadata, x, y)
      return {
        ...state,
        currentCol: x,
        currentRow: y,
        currentColTreeCode: currentMetadata && currentMetadata.col,
        currentRowTreeCode: currentMetadata && currentMetadata.row,
        selectionCol: x,
        selectionRow: y,
        mouseDown: true,
        editorActive: clickIn,
        editorMode: clickIn ? 'click' : 'none',
        currentOriginalData: clickIn ? originalData : '',
        currentChangedData: clickIn
          ? (state.currentChangedData ? state.currentChangedData : originalData)
          : ''
      }
    }
    case 'mouseUp': {
      return { ...state, mouseDown: false }
    }
    case 'resize': {
      const { width, height } = action
      const tableHeight = Math.max(tableMinHeight, height || state.tableHeight)
      const tableWidth = width || state.tableWidth
      return { ...state, tableWidth: tableWidth, tableHeight: tableHeight }
    }
    case 'toggleCollapse': {
      const { row } = action
      const { metadata, collapsedRows } = state
      const alreadyCollapsed = collapsedRows.indexOf(row) >= 0
      const newCollapsedRows = alreadyCollapsed
        ? collapsedRows.filter(x => x !== row)
        : [...collapsedRows, row]
      const hiddenRows = getHiddenRows(metadata, newCollapsedRows)
      return { ...state, collapsedRows: newCollapsedRows, hiddenRows }
    }
    case 'select': {
      const { x, y } = action
      if (state.fixedHeader && (y < state.topHeaderRowCount || state.currentRow < state.topHeaderRowCount)) return state
      return {
        ...state,
        selectionCol: x,
        selectionRow: y
      }
    }
    case 'keydown': {
      const { key, rtl, shiftKey } = action
      const dir = rtl ? -1 : 1
      const arrowMovesSelection = state.editorMode === 'none' || state.editorMode === 'char' || state.editorMode === 'cancelled'
      switch (key) {
        case 'ArrowUp': {
          return arrowMovesSelection ? applySelectionChange(state, 0, -1, shiftKey) : state
        }
        case 'ArrowLeft': {
          return arrowMovesSelection ? applySelectionChange(state, -dir, 0, shiftKey) : state
        }
        case 'ArrowRight': {
          return arrowMovesSelection ? applySelectionChange(state, dir, 0, shiftKey) : state
        }
        case 'ArrowDown': {
          return arrowMovesSelection ? applySelectionChange(state, 0, 1, shiftKey) : state
        }
        case 'Tab': {
          return applySelectionChange(state, shiftKey ? -dir : dir, 0)
        }
        case 'Enter': {
          if (!state.readOnly && (state.editorMode === 'none' || state.editorMode === 'cancelled')) {
            const originalData = getCurrentItemData(state)
            return {
              ...state,
              editorMode: 'enter',
              editorActive: true,
              currentOriginalData: originalData,
              currentChangedData: originalData
            }
          }
          return applySelectionChange(state, 0, shiftKey ? -1 : 1)
        }
        case 'Escape': {
          if (state.editorActive && state.editorMode !== 'none') {
            return { ...state, editorActive: false, editorMode: 'cancelled', ...defaultEditorValues }
          }
          break
        }
        case 'Delete':
        case 'Backspace': {
          if (!state.readOnly && state.editorMode === 'none') {
            return deleteDataItemValues(state)
          }
          break
        }
      }
      break
    }
    case 'keyPressed': {
      const canEdit = !state.readOnly && (state.editorMode === 'none' || state.editorMode === 'cancelled')
      if (!canEdit) {
        return state
      }
      const originalData = getCurrentItemData(state)
      return {
        ...state,
        editorMode: 'char',
        editorActive: true,
        currentOriginalData: originalData,
        currentChangedData: originalData,
        enteredChar: action.value
      }
    }
    case 'copy': {
      selectionToClipboard(state, action.clipboardData)
      return state
    }
    case 'paste': {
      if (!state.readOnly && state.editorMode === 'none') {
        return pasteDataFromClipboard(state, action.data)
      }
      return state
    }
    case 'columnSizeChanged': {
      return {
        ...state,
        columnSizes: state.columnSizes.map((currentWidth, i) => action.colIndex === i ? action.width : currentWidth)
      }
    }
    case 'clearChanges': {
      return { ...state, changedCells: [] }
    }
    case 'updateCurrentChange': {
      // Change can come in 2 forms - only data or object with data and selection
      if (action.data.value !== undefined) {
        return {
          ...state,
          currentChangedData: action.data.value,
          inputSelectionStart: action.data.start,
          inputSelectionEnd: action.data.end
        }
      }
      return { ...state, currentChangedData: action.data }
    }
    case 'updateSelection': {
      if (action.data.start && action.data.end) {
        return {
          ...state,
          inputSelectionStart: action.data.start,
          inputSelectionEnd: action.data.end
        }
      }
      return state
    }
    case 'reverseTransform': { // Called when editor cell is initialized to load data to input from stored value
      if (!state.reverseTransformed && typeof action.data === 'object') {
        const { getValue, validateValue } = action.data
        let transformed = getValue(state.currentChangedData, validateValue(state.enteredChar) ? state.enteredChar : '')
        if (transformed.start === undefined) transformed.start = transformed.value.length + 1
        if (transformed.end === undefined) transformed.end = transformed.value.length + 1
        return { ...state,
          currentChangedData: transformed.value,
          currentOriginalData: getValue(state.currentOriginalData, '').value,
          inputSelectionStart: transformed.start,
          inputSelectionEnd: transformed.end,
          persistTransform: transformed.persistTransform,
          reverseTransformed: true }
      }
      return state
    }
  }
  return state
}

function getColumnSizes (columnSizes, columnCount, prevCols, newCols) {
  if (columnSizes.length !== columnCount) {
    prevCols = prevCols.map(x => x.col || '')
    newCols = newCols.map(x => x.col || '')
    columnSizes = Array(columnCount).fill(columnMinAutoWidth).map((x, i) => {
      const col = newCols[i]
      const prevIndex = typeof col === 'string' ? prevCols.indexOf(col) : -1
      let size = prevIndex >= 0 && prevIndex < columnSizes.length ? columnSizes[prevIndex] : x
      if (size < minColumnWidth) size = x
      return size
    })
  }
  return columnSizes
}

function applySelectionChange (state, colDir, rowDir, shiftKey) {
  const { metadata, currentCol, currentRow, selectionCol, selectionRow, hiddenRows } = state
  if (state.fixedHeader && shiftKey && (state.currentRow < state.topHeaderRowCount || state.currentRow + rowDir < state.topHeaderRowCount)) return state
  const currentSelectionMetadata = getMetadataByCoords(metadata, selectionCol, selectionRow)
  // Updates the values of current change and changedCells
  state = updateChangedCell(state)
  if (!state.allowSelectionChange) {
    return state
  }

  if (currentSelectionMetadata) {
    const targetCol = currentSelectionMetadata.xCoord + (colDir > 0 ? currentSelectionMetadata.colSpan : colDir)
    let targetRow = currentSelectionMetadata.yCoord
    do {
      targetRow += (rowDir > 0 ? currentSelectionMetadata.rowSpan : rowDir)
    } while (hiddenRows[targetRow])

    const targetMetadata = getMetadataByCoords(metadata, targetCol, targetRow)
    if (targetMetadata) {
      const currentMetadata = getMetadataByCoords(metadata, currentCol, currentRow)
      const selection = getSelectionState(targetMetadata, shiftKey, currentMetadata)
      return {
        ...state,
        ...selection
      }
    }
  }
  if (state.editorActive !== false || state.editorMode !== 'none') {
    return {
      ...state,
      editorActive: false,
      editorMode: 'none'
    }
  }
  return state
}

function getSelectionState (targetMetadata, shiftKey, currentMetadata) {
  const state = {}
  state.editorActive = false
  state.editorMode = 'none'

  if (shiftKey) {
    // with shift - move only selection coords instead but only if inside a body
    if (targetMetadata.col && targetMetadata.row && currentMetadata.col && currentMetadata.row) {
      state.selectionCol = targetMetadata.xCoord
      state.selectionRow = targetMetadata.yCoord
    }
  } else { // without shift - move both, currentCol/Row and selectionCol/Row
    state.currentCol = targetMetadata.xCoord
    state.currentRow = targetMetadata.yCoord
    state.currentColTreeCode = targetMetadata.col
    state.currentRowTreeCode = targetMetadata.row
    state.selectionCol = targetMetadata.xCoord
    state.selectionRow = targetMetadata.yCoord
  }
  return state
}

function getHiddenRows (metadata, collapsedRows) {
  return metadata.map((row) => {
    const rowHeader = row[0]
    return (rowHeader.row && !!collapsedRows.find(x => rowHeader.row.startsWith(x + '.'))) || false
  })
}

function getCurrentItemData (state) {
  const foundDataItem = state.dataItems.find(x => x.rowTreeCode === state.currentRowTreeCode && x.columnTreeCode === state.currentColTreeCode)
  return foundDataItem && foundDataItem.data ? foundDataItem.data : ''
}

function updateChangedCell (state) {
  const {
    currentOriginalData,
    currentChangedData,
    currentColTreeCode,
    currentRowTreeCode,
    colIds,
    rowIds,
    persistTransform
  } = state
  const changedCells = state.changedCells
  let transformedValue = state.currentChangedData
  if (typeof persistTransform === 'function') transformedValue = persistTransform(currentChangedData)
  // The persistent transformer refused data = prevent move
  if (transformedValue === 'invalid') {
    return {
      ...state,
      allowSelectionChange: false
    }
  }
  if (currentChangedData !== currentOriginalData) {
    const change = {
      headerColId: colIds[currentColTreeCode],
      headerRowId: rowIds[currentRowTreeCode],
      columnTreeCode: currentColTreeCode,
      rowTreeCode: currentRowTreeCode,
      data: transformedValue,
      errors: null,
      warnings: null
    }
    changedCells.push(change)
  }

  return { ...state, ...defaultEditorValues }
}

function deleteDataItemValues (state) {
  const { currentCol, currentRow, selectionCol, selectionRow } = state
  if (currentCol && currentRow) {
    const selection = getSelectionRectangle(selectionCol, currentCol, selectionRow, currentRow)
    const rows = selection.bottom - selection.top + 1
    const cols = selection.right - selection.left + 1
    const data = Array(rows).fill(Array(cols).fill(null))
    return updateDataItems(state, data, selection.left, selection.top)
  }
  return state
}

function pasteDataFromClipboard (state, data) {
  return updateDataItems(state, data, state.currentCol, state.currentRow)
}

function updateDataItems (state, data, left, top) {
  if (data && data.length) {
    const { metadata, dataItems } = state
    const changes = []
    for (let rowOffset = 0; rowOffset < data.length; rowOffset++) {
      const rowData = data[rowOffset]
      if (!rowData) continue
      for (let colOffset = 0; colOffset < rowData.length; colOffset++) {
        const cellData = rowData[colOffset]
        const col = left + colOffset
        const row = top + rowOffset
        const currentMetadata = getMetadataByCoords(metadata, col, row)
        if (currentMetadata && !currentMetadata.readOnly && !currentMetadata.isDisabled &&
          currentMetadata.col && currentMetadata.row) {
          const change = {
            headerColId: currentMetadata.colId,
            headerRowId: currentMetadata.rowId,
            columnTreeCode: currentMetadata.col,
            rowTreeCode: currentMetadata.row
          }
          const { value, error } = validate(currentMetadata.reportDataType, cellData)
          if (!error) {
            change.data = value
            change.errors = null
            change.warnings = null
          } else {
            const currentData = dataItems.find(x => x.columnTreeCode === currentMetadata.col && x.rowTreeCode === currentMetadata.row)
            change.data = currentData && currentData.data // original value
            change.errors = error
            change.warnings = null
          }
          changes.push(change)
        }
      }
    }
    if (changes.length > 0) {
      return { ...state, changedCells: changes }
    }
  }
  return state
}

export {
  initialState,
  reducer
}



// WEBPACK FOOTER //
// ./src/components/report-tables/core/reducer.js